import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Lock } from "react-feather";
import { toast } from "react-toastify";
import { getTransactionsByCreatedDate } from "../../../Api/apiUtils";
import { useHasEmailAccess } from "../../../utils/emailAccessConfig";
import { isSalesRestrictedUser } from "../../../utils/permissions";
import {
  Page,
  PageHeader,
  Card,
  Button,
  Field,
  TextInput,
  Toolbar,
  Table,
  DescriptionList,
  Loader,
  EmptyState,
} from "../../../ui";
import MultiSelectFilter from "./multiSelectFilter";
import "./transactions.scss";

interface Transaction {
  studentId: string;
  name: string;
  email: string;
  contact: string;
  timeZone: string;
  courseName: string;
  level: string;
  classes: number;
  amount: string;
  paymentVia: string;
  paymentType: string;
  gender: string;
  ageGroup: string;
  state: string;
  salesAgent: string;
  academicExpert: string;
  academicExpertName?: string;
  salesAgentName?: string;
  createdBy?: string;
  categoryName?: string;
  trialClassDate: string;
  packageRecommendedBy: string;
  studentType: string;
  transactionType?: string;
  platform: string;
  teacherName: string;
  exchange_rate?: string | number;
  currency?: string;
  totalPrice?: string | number;
  discountedPrice?: string | number;
  unit_price?: string | number;
  invoice_number?: string;
  courseList?: Array<{
    level?: string | number | null;
    classesToPurchase?: string | number | null;
    unit_price?: string | number | null;
    discountedPrice?: string | number | null;
  }>;
}

/** RFC 4180: wrap in double quotes and escape internal quotes so commas/newlines stay inside the cell. */
const escapeCsvCell = (value: unknown): string => {
  const s = value === null || value === undefined ? "" : String(value);
  return `"${s.replace(/"/g, '""')}"`;
};

const MAX_DATE_RANGE_DAYS = 31;

const toLocalYmd = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const getDaysBetween = (fromStr: string, toStr: string): number => {
  const from = new Date(fromStr);
  const to = new Date(toStr);
  const diffMs = to.getTime() - from.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

const US_TZS = new Set([
  "America/New_York",
  "America/Detroit",
  "America/Chicago",
  "America/Indiana/Indianapolis",
  "America/Indiana/Knox",
  "America/Indiana/Marengo",
  "America/Indiana/Petersburg",
  "America/Indiana/Tell_City",
  "America/Indiana/Vevay",
  "America/Indiana/Vincennes",
  "America/Indiana/Winamac",
  "America/Kentucky/Louisville",
  "America/Kentucky/Monticello",
  "America/North_Dakota/Center",
  "America/North_Dakota/New_Salem",
  "America/North_Dakota/Beulah",
  "America/Menominee",
  "America/Boise",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "America/Adak",
  "America/Honolulu",
  "America/Sitka",
  "America/Juneau",
  "America/Nome",
  "America/Yakutat",
  "America/Metlakatla",
  "Pacific/Honolulu",
]);

function isUsTimezone(tz: string): boolean {
  if (tz.startsWith("US/")) return true;
  return US_TZS.has(tz);
}

function resolveRegion(timeZoneRaw: string | undefined | null): string {
  if (!timeZoneRaw || typeof timeZoneRaw !== "string") return "ROW";
  const tz = timeZoneRaw.trim();
  if (tz === "Asia/Kolkata" || tz === "Asia/Calcutta") return "INDIA";
  if (isUsTimezone(tz)) return "US";
  return "ROW";
}

const REGION_OPTIONS = [
  { value: "", label: "All Regions" },
  { value: "INDIA", label: "India" },
  { value: "US", label: "US" },
  { value: "ROW", label: "ROW" },
];

const STUDENT_TYPE_OPTIONS = [
  { value: "", label: "All" },
  { value: "New", label: "New" },
  { value: "Renewal", label: "Renewal" },
  { value: "New -(CrossSales)", label: "New -(CrossSales)" },
];

const CLASS_TYPE_OPTIONS = [
  { value: "", label: "All" },
  { value: "paid", label: "Paid" },
  { value: "free", label: "Free Class" },
];

/** Comma-separated `level` from each courseList item; "-" if none. */
const getLevelsFromCourseList = (txn: { courseList?: Transaction["courseList"] }): string => {
  const list = txn?.courseList;
  if (!Array.isArray(list) || list.length === 0) return "-";
  const levels = list
    .map((item) => item?.level)
    .filter((v) => v !== null && v !== undefined && String(v).trim() !== "");
  if (levels.length === 0) return "-";
  return levels.map((v) => String(v)).join(", ");
};

/** Sum of `classesToPurchase` across courseList; "-" if no usable numbers. */
const getClassesPurchasedFromCourseList = (txn: { courseList?: Transaction["courseList"] }): string => {
  const list = txn?.courseList;
  if (!Array.isArray(list) || list.length === 0) return "-";
  let sum = 0;
  let hadNumeric = false;
  for (const item of list) {
    if (item?.classesToPurchase === null || item?.classesToPurchase === undefined || item?.classesToPurchase === "") {
      continue;
    }
    const n = Number(item.classesToPurchase);
    if (!Number.isNaN(n)) {
      sum += n;
      hadNumeric = true;
    }
  }
  return hadNumeric ? String(sum) : "-";
};

/**
 * Per-class price from courseList: [Preparatory - 900, Intermediate - 1050]
 * Falls back to top-level unit_price when courseList has no unit_price rows.
 */
const getPerClassPriceDisplay = (txn: {
  courseList?: Transaction["courseList"];
  unit_price?: string | number;
  level?: string;
}): string => {
  const list = txn?.courseList;
  if (Array.isArray(list) && list.length > 0) {
    const parts = list
      .map((item) => {
        if (item?.unit_price === null || item?.unit_price === undefined || item?.unit_price === "") {
          return null;
        }
        const level = item?.level != null && String(item.level).trim() !== ""
          ? String(item.level).trim()
          : "Level";
        return `${level} - ${item.unit_price}`;
      })
      .filter(Boolean) as string[];
    if (parts.length > 0) return `[${parts.join(", ")}]`;
  }
  if (txn.unit_price !== null && txn.unit_price !== undefined && txn.unit_price !== "") {
    const level = txn.level?.trim() ? txn.level.trim() : "Level";
    return `[${level} - ${txn.unit_price}]`;
  }
  return "-";
};

const getSalesAgentDisplay = (txn: {
  salesAgentName?: string;
  createdBy?: string;
  salesAgent?: string;
}): string => {
  const value = txn.salesAgentName || txn.createdBy || txn.salesAgent;
  return value?.trim() ? value.trim() : "-";
};

const isInrCurrency = (currency: unknown): boolean =>
  String(currency ?? "").trim().toUpperCase() === "INR";

/** INR rows should always use exchange rate 1 when API sends a different value. */
const getEffectiveExchangeRate = (txn: {
  currency?: string;
  exchange_rate?: string | number;
}): number | null => {
  const raw = txn.exchange_rate;
  if (raw === null || raw === undefined || raw === "") return null;
  const parsed = parseFloat(String(raw));
  if (Number.isNaN(parsed)) return null;
  if (isInrCurrency(txn.currency) && parsed !== 1) return 1;
  return parsed;
};

const formatExchangeRate = (txn: { currency?: string; exchange_rate?: string | number }): string => {
  const effective = getEffectiveExchangeRate(txn);
  return effective === null ? "-" : String(effective);
};

const Transactions: React.FC = () => {
  const navigate = useNavigate();
  const { ready: accessReady, allowed: emailAllowed } = useHasEmailAccess("transactions");
  const salesAllowed = isSalesRestrictedUser();
  const isAllowed = emailAllowed || salesAllowed;

  const [fromDate, setFromDate] = useState<string>(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return toLocalYmd(firstDay);
  });
  const [toDate, setToDate] = useState<string>(() =>
    new Date().toISOString().split("T")[0]
  );
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedStudentTypes, setSelectedStudentTypes] = useState<string[]>([]);
  const [selectedClassTypes, setSelectedClassTypes] = useState<string[]>([]);
  const [openFilter, setOpenFilter] = useState<"region" | "studentType" | "classType" | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [fetched, setFetched] = useState<boolean>(false);
  const [fetchedFromDate, setFetchedFromDate] = useState<string>("");
  const [fetchedToDate, setFetchedToDate] = useState<string>("");

  useEffect(() => {
    if (!openFilter) return;
    const close = () => setOpenFilter(null);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [openFilter]);

  const getTransactionType = (txn: any): string => {
    if (!txn || typeof txn !== "object") return "";
    const direct =
      txn.transactionType ??
      txn.transactiontype ??
      txn.transaction_type ??
      txn.TransactionType ??
      txn.TRANSACTION_TYPE;
    if (typeof direct === "string" && direct.trim()) return direct.trim();
    // Temporary fallback for older payloads that don't include transactionType.
    if (txn.studentType === "PAID") return "Renewal";
    if (typeof txn.studentType === "string" && txn.studentType.trim()) return "New";
    return "";
  };

  const filteredTransactions = useMemo(() => {
    return transactions.filter((txn: any) => {
      const matchRegion = selectedRegions.length === 0 || selectedRegions.includes(resolveRegion(txn.timeZone));
      const matchStudentType =
        selectedStudentTypes.length === 0 ||
        selectedStudentTypes.some((t) => getTransactionType(txn).toLowerCase() === t.toLowerCase());
      const matchClassType =
        selectedClassTypes.length === 0 ||
        selectedClassTypes.some((ct) =>
          ct === "free" ? txn.isFree === true || txn.isFree === "true" : txn.isFree !== true && txn.isFree !== "true"
        );
      return matchRegion && matchStudentType && matchClassType;
    });
  }, [transactions, selectedRegions, selectedStudentTypes, selectedClassTypes]);

  const fetchTransactions = async (from: string, to: string) => {
    setLoading(true);
    try {
      const data = await getTransactionsByCreatedDate(from, to);
      const list = Array.isArray(data) ? data : data?.data || data?.content || [];
      setTransactions(list);
      setFetched(true);
      setFetchedFromDate(from);
      setFetchedToDate(to);
      if (list.length === 0) {
        toast.info("No transactions found for the selected date range.");
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      toast.error("Failed to fetch transactions.");
      setTransactions([]);
      setFetched(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!accessReady || !isAllowed) return;
    fetchTransactions(fromDate, toDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessReady, isAllowed]);

  if (!accessReady && !salesAllowed) {
    return (
      <Page>
        <Loader label="Loading permissions…" />
      </Page>
    );
  }

  if (!isAllowed) {
    return (
      <Page>
        <PageHeader eyebrow="Sales" title="Transactions" />
        <EmptyState
          icon={<Lock size={22} aria-hidden />}
          title="Access denied"
          description="You do not have permission to view transactions. This page requires finance access."
          action={
            <Button variant="primary" onClick={() => navigate("/pages/student-profile")}>
              Go to Dashboard
            </Button>
          }
        />
      </Page>
    );
  }

  const handleFetch = () => {
    if (!fromDate || !toDate) {
      toast.warning("Please select both From Date and To Date.");
      return;
    }
    if (fromDate > toDate) {
      toast.warning("From Date cannot be after To Date.");
      return;
    }
    const days = getDaysBetween(fromDate, toDate);
    if (days > MAX_DATE_RANGE_DAYS) {
      toast.warning(`Date range cannot exceed ${MAX_DATE_RANGE_DAYS} days.`);
      return;
    }
    fetchTransactions(fromDate, toDate);
  };

  const downloadCSV = () => {
    if (filteredTransactions.length === 0) {
      toast.warning("No transactions to download.");
      return;
    }
    const headers = [
      "#",
      "Created At",
      "Invoice Number",
      "Student ID",
      "Student Name",
      "Payment Type",
      "Email",
      "Contact Number",
      "Timezone",
      "Region",
      "Course Name",
      "Level",
      "Classes Purchased",
      "Currency",
      "Total Price",
      "Per Class Price",
      "Exchange Rate",
      "Revenue",
      "Gender",
      "Age Group",
      "State",
      "Platform",
      "Package Recommended By",
      "Academic Expert",
      "Sales Agent",
      "Category",
    ];
    const csvRows = [
      headers.map(escapeCsvCell).join(","),
      ...filteredTransactions.map((txn: any, index: number) => {
        const salesAgent = getSalesAgentDisplay(txn);
        const cells = [
          index + 1,
          formatDate(txn.createdAt) || "",
          txn.invoice_number || "",
          txn.studentId || "",
          txn.name || "",
          getTransactionType(txn),
          txn.email || "",
          txn.countryCode ? `${txn.countryCode} ${txn.contact}` : txn.contact || "",
          txn.timeZone || "",
          resolveRegion(txn.timeZone),
          txn.courseName || "",
          getLevelsFromCourseList(txn),
          getClassesPurchasedFromCourseList(txn),
          txn.currency || "",
          txn.totalPrice || "",
          getPerClassPriceDisplay(txn) !== "-" ? getPerClassPriceDisplay(txn) : "",
          formatExchangeRate(txn) !== "-" ? formatExchangeRate(txn) : "",
          formatRevenue(txn) !== "-" ? formatRevenue(txn) : "",
          txn.gender || "",
          txn.ageGroup || "",
          txn.state || "",
          txn.platform || "",
          txn.packageRecommendedBy || "",
          txn.academicExpertName || txn.academicExpert || "",
          salesAgent !== "-" ? salesAgent : "",
          txn.categoryName || "",
        ];
        return cells.map(escapeCsvCell).join(",");
      }),
    ];
    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const fromLabel = fetchedFromDate || fromDate;
    const toLabel = fetchedToDate || toDate;
    link.download = `transactions_${fromLabel}_to_${toLabel}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    // toast.success("CSV downloaded successfully.");
  };

  const getRevenue = (txn: any) => {
    if (txn.isFree === true || txn.isFree === "true") return 0;
    const totalPrice = parseFloat(txn.totalPrice);
    const exchangeRate = getEffectiveExchangeRate(txn);
    if (isNaN(totalPrice)) return null;
    const revenue = (exchangeRate ?? 1) * totalPrice;
    return revenue;
  };

  const formatRevenue = (txn: any) => {
    const revenue = getRevenue(txn);
    if (revenue === null) return "-";
    return `${revenue.toFixed(2)}`.trim();
  };

  const totalRevenue = filteredTransactions.reduce((sum, txn) => {
    const rev = getRevenue(txn);
    return sum + (rev ?? 0);
  }, 0);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return dateStr;
    }
  };
  const fetchedPeriodLabel =
    fetchedFromDate && fetchedToDate
      ? `${fetchedFromDate} to ${fetchedToDate}`
      : null;

  const activeFilters = [
    selectedRegions.length > 0 && `Region: ${selectedRegions.join(", ")}`,
    selectedStudentTypes.length > 0 && `Type: ${selectedStudentTypes.join(", ")}`,
    selectedClassTypes.length > 0 && `Class: ${selectedClassTypes.join(", ")}`,
  ].filter(Boolean);

  return (
    <Page>
      <PageHeader
        eyebrow="Sales"
        title="Transactions"
        subtitle={`Payments created in a date range (up to ${MAX_DATE_RANGE_DAYS} days), with revenue in INR.`}
        actions={
          <Button variant="secondary" icon={<Download size={16} aria-hidden />} onClick={downloadCSV}>
            Download CSV
          </Button>
        }
      />

      <Card>
        <Toolbar>
          <Field label="From date">
            {(ctl) => <TextInput {...ctl} type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />}
          </Field>
          <Field label="To date">
            {(ctl) => <TextInput {...ctl} type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />}
          </Field>
          <div className="transactions-fetch">
            <Button variant="primary" onClick={handleFetch} loading={loading}>
              {loading ? "Fetching…" : "Get data"}
            </Button>
          </div>
          <Field label="Region">
            {(ctl) => (
              <MultiSelectFilter
                {...ctl}
                options={REGION_OPTIONS.filter((r) => r.value)}
                selected={selectedRegions}
                onChange={setSelectedRegions}
                placeholder="All regions"
                open={openFilter === "region"}
                onToggle={() => setOpenFilter((p) => (p === "region" ? null : "region"))}
                onClose={() => setOpenFilter(null)}
              />
            )}
          </Field>
          <Field label="Payment type">
            {(ctl) => (
              <MultiSelectFilter
                {...ctl}
                options={STUDENT_TYPE_OPTIONS.filter((s) => s.value)}
                selected={selectedStudentTypes}
                onChange={setSelectedStudentTypes}
                placeholder="All"
                open={openFilter === "studentType"}
                onToggle={() => setOpenFilter((p) => (p === "studentType" ? null : "studentType"))}
                onClose={() => setOpenFilter(null)}
              />
            )}
          </Field>
          <Field label="Class type">
            {(ctl) => (
              <MultiSelectFilter
                {...ctl}
                options={CLASS_TYPE_OPTIONS.filter((c) => c.value)}
                selected={selectedClassTypes}
                onChange={setSelectedClassTypes}
                placeholder="All"
                open={openFilter === "classType"}
                onToggle={() => setOpenFilter((p) => (p === "classType" ? null : "classType"))}
                onClose={() => setOpenFilter(null)}
              />
            )}
          </Field>
        </Toolbar>
      </Card>

      <Card>
        <DescriptionList
          items={[
            {
              label: `Total revenue${fetchedPeriodLabel ? ` (${fetchedPeriodLabel})` : ""}`,
              value: (
                <span className="ds-num ds-strong">
                  ₹{" "}
                  {fetched
                    ? totalRevenue.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    : "-"}
                </span>
              ),
            },
            {
              label: "Transactions",
              value: fetched && transactions.length > 0 ? (
                <span className="ds-num">
                  {filteredTransactions.length}
                  {activeFilters.length > 0 && <span className="ds-text-subtle"> of {transactions.length}</span>}
                </span>
              ) : (
                "-"
              ),
            },
            { label: "Filtered by", value: activeFilters.length > 0 ? activeFilters.join(" | ") : "No filters" },
          ]}
        />
      </Card>

      {loading ? (
        <Loader label="Loading transactions…" />
      ) : fetched && transactions.length === 0 ? (
        <EmptyState
          title="No transactions in this date range"
          description="Pick a different From and To date and select Get data."
        />
      ) : transactions.length > 0 && filteredTransactions.length === 0 ? (
        <EmptyState
          title="No transactions match these filters"
          description="Clear the Region, Payment type or Class type filter to see more transactions."
        />
      ) : filteredTransactions.length > 0 ? (
        <Table caption="Transactions" stickyHeader wrapClassName="transactions-scroll">
          <thead>
            <tr>
              <th className="ds-num">#</th>
              <th>Created at</th>
              <th>Invoice number</th>
              <th>Student ID</th>
              <th>Student name</th>
              <th>Payment type</th>
              <th>Email</th>
              <th>Contact number</th>
              <th>Timezone</th>
              <th>Region</th>
              <th>Course name</th>
              <th>Level</th>
              <th className="ds-num">Classes purchased</th>
              <th>Currency</th>
              <th className="ds-num">Total price</th>
              <th>Per class price</th>
              <th className="ds-num">Exchange rate</th>
              <th className="ds-num">Revenue (INR)</th>
              <th>Gender</th>
              <th>Age group</th>
              <th>State</th>
              <th>Platform</th>
              <th>Package recommended by</th>
              <th>Academic expert</th>
              <th>Sales agent</th>
              <th>Category</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransactions.map((txn: any, index: number) => (
              <tr key={index}>
                <td className="ds-num ds-text-subtle">{index + 1}</td>
                <td className="transactions-nowrap">{formatDate(txn.createdAt) || "-"}</td>
                <td>{txn.invoice_number || "-"}</td>
                <td>{txn.studentId || "-"}</td>
                <td className="ds-strong">{txn.name || "-"}</td>
                <td>{getTransactionType(txn) || "-"}</td>
                <td>{txn.email || "-"}</td>
                <td className="transactions-nowrap">
                  {txn.countryCode ? `${txn.countryCode} ${txn.contact}` : txn.contact || "-"}
                </td>
                <td>{txn.timeZone || "-"}</td>
                <td>{resolveRegion(txn.timeZone)}</td>
                <td>{txn.courseName || "-"}</td>
                <td>{getLevelsFromCourseList(txn)}</td>
                <td className="ds-num">{getClassesPurchasedFromCourseList(txn)}</td>
                <td>{txn.currency || "-"}</td>
                <td className="ds-num">{txn.totalPrice || "-"}</td>
                <td>{getPerClassPriceDisplay(txn)}</td>
                <td className="ds-num">{formatExchangeRate(txn)}</td>
                <td className="ds-num ds-strong">{formatRevenue(txn)}</td>
                <td>{txn.gender || "-"}</td>
                <td>{txn.ageGroup || "-"}</td>
                <td>{txn.state || "-"}</td>
                <td>{txn.platform || "-"}</td>
                <td>{txn.packageRecommendedBy || "-"}</td>
                <td>{txn.academicExpertName || txn.academicExpert || "-"}</td>
                <td>{getSalesAgentDisplay(txn)}</td>
                <td>{txn.categoryName || "-"}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}
    </Page>
  );
};

export default Transactions;